import { useCallback, useState } from 'react';
import { useApolloClient, ApolloError } from '@apollo/client';
import {
  MatchType,
  MedicineDetailResult,
  MedicineDetailsDocument,
  MedicineDetailsQuery,
  MedicineDetailsQueryVariables,
  Wholesale,
} from 'features/graphql';
import { graphQlErrorMessage } from 'utils/miscellaneous';
import { useAccountContext } from 'features/auth/AccountProvider';
import { buildPriceShowCsv, downloadCsv } from './priceShowCsv';

interface UsePriceShowCsvExportParams {
  selected: string;
  currentYear: number;
  previousYear: number;
  wholesales: Wholesale[] | undefined;
  wholesalesError: ApolloError | undefined;
  facilityStatus: string;
}

/**
 * 価格表のCSV出力処理（D4 step6: PriceShowList から handleCsvExport と関連stateを分離）。
 * 取得〜ダウンロードのロジック・エラーメッセージは分離前と不変。
 */
export const usePriceShowCsvExport = ({
  selected,
  currentYear,
  previousYear,
  wholesales,
  wholesalesError,
  facilityStatus,
}: UsePriceShowCsvExportParams) => {
  // CSV出力用クライアント。バックエンドで卸価格があるものに絞り込むため、
  // pageSize:-1 の1リクエストで取得する（表示中の一覧キャッシュと干渉させないため client.query を使う）。
  const apolloClient = useApolloClient();
  const accountContext = useAccountContext();
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [showCsvConfirm, setShowCsvConfirm] = useState(false);

  // CSV出力ハンドラー
  // 検索条件に依らず、卸価格がある全品目を価格表画面の表示内容のままCSV出力する。
  // バックエンドで卸価格(BILL.PACKAGING_PRICE)があるものだけに絞り込むため、
  // pageSize:-1 の1リクエストで取得できる（対象は数千件規模に収まる）。
  const handleCsvExport = useCallback(async () => {
    setCsvError(null);
    // 卸情報が未取得のままだと卸価格・割引率・購入実績の列が欠落するため、取得完了まで出力しない。
    if (wholesalesError || !wholesales?.length) {
      setCsvError(
        '卸情報を取得できていないため、CSVを出力できません。画面を再読み込みのうえ、再度お試しください。'
      );
      return;
    }
    setCsvLoading(true);
    try {
      const result = await apolloClient.query<MedicineDetailsQuery, MedicineDetailsQueryVariables>({
        query: MedicineDetailsDocument,
        variables: {
          page: 0,
          pageSize: -1, // 全件取得（バックエンドで卸価格ありに絞り込み済み）
          medicineTypes: ['外', '注', '内', '歯'], // 検索条件に依らず全区分を対象
          matchType: selected as MatchType,
          currentYear,
          previousYear,
          isForPriceDisplay: true,
          hasPackagingPrice: true, // 卸価格があるものだけをバックエンドで絞り込む
        },
        fetchPolicy: 'no-cache', // 表示中の一覧キャッシュと干渉させない
      });

      const medicines = result.data?.medicineDetails?.medicines ?? [];
      // 念のためクライアント側でも卸価格の有無を確認（年度境界ケースの保険）
      const targets: MedicineDetailResult[] = medicines.filter((medicine) =>
        (medicine.deals ?? []).some((deal) => deal?.packagingPrice != null)
      );

      if (targets.length === 0) {
        setCsvError('卸価格のあるデータがありません。');
        return;
      }

      const csv = buildPriceShowCsv(targets, wholesales ?? [], facilityStatus);

      const now = new Date();
      const yyyymmdd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
        now.getDate()
      ).padStart(2, '0')}`;
      const institutionName = accountContext.account?.institution?.institutionName ?? '価格表';
      downloadCsv(`価格表_${institutionName}_${yyyymmdd}.csv`, csv);
    } catch (e) {
      setCsvError(graphQlErrorMessage(e as ApolloError) ?? 'CSV出力に失敗しました。');
    } finally {
      setCsvLoading(false);
    }
  }, [
    apolloClient,
    selected,
    currentYear,
    previousYear,
    wholesales,
    wholesalesError,
    facilityStatus,
    accountContext,
  ]);

  return { csvLoading, csvError, setCsvError, showCsvConfirm, setShowCsvConfirm, handleCsvExport };
};
